// =============================================================================
// SecurityControls.jsx — KlassDoc Sprint 6.5 Wave 2.1
// -----------------------------------------------------------------------------
// Shared sub-components used by both UploadPropertiesModal and ManageSecurityModal.
// Extracted into a single source of truth so future changes propagate everywhere.
//
// EXPORTS
//   ACCESS_LEVELS         — array of access level definitions with colors/icons
//   AccessLevelDropdown   — color-coded dropdown to pick an access level
//   PrincipalPicker       — type-ahead search for users/groups via /api/permissions/principals
//   AclEntryRow           — single row in the permissions list (avatar + name + dropdown + remove)
//   getInitials           — helper for avatar generation
// =============================================================================

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";

// ─── Access level definitions (Eva's preference: color-coded badges) ───
export const ACCESS_LEVELS = [
  { value: "full",       labelFr: "Accès complet",    labelEn: "Full Access",  color: "#5CB88A", icon: "ShieldCheck" },
  { value: "read_write", labelFr: "Lecture/Écriture", labelEn: "Read/Write",   color: "#5E8AD4", icon: "Edit3" },
  { value: "read_only",  labelFr: "Lecture seule",    labelEn: "Read Only",    color: "#D4B85E", icon: "Eye" },
  { value: "no_access",  labelFr: "Aucun accès",      labelEn: "No Access",    color: "#D46A6A", icon: "ShieldOff" },
];

export function getInitials(name) {
  return (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}
export function getAvatarColor(name) {
  return `hsl(${[...(name || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360}, 40%, 30%)`;
}
function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }

// =============================================================================
// AccessLevelDropdown — color-coded select for permission rows
// =============================================================================
export function AccessLevelDropdown({ value, onChange, lang, disabled, THEME }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const current = ACCESS_LEVELS.find(l => l.value === value) || ACCESS_LEVELS[2];
  const Icon = lucide[current.icon] || lucide.Shield;

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 150 }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", padding: "6px 12px", borderRadius: 6,
          background: current.color + "1A",
          border: `1px solid ${current.color}40`,
          color: current.color,
          fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1, fontFamily: "inherit",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Icon size={13} />
          {fmt(lang, current.labelFr, current.labelEn)}
        </span>
        {!disabled && <lucide.ChevronDown size={12} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            style={{
              position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 100,
              background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`,
              borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              overflow: "hidden", minWidth: 180,
            }}
          >
            {ACCESS_LEVELS.map(lvl => {
              const LIcon = lucide[lvl.icon] || lucide.Shield;
              const isCurrent = lvl.value === value;
              return (
                <button
                  key={lvl.value} type="button"
                  onClick={() => { onChange(lvl.value); setOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "10px 14px", border: "none", background: isCurrent ? lvl.color + "1A" : "transparent",
                    color: lvl.color, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    textAlign: "left", fontFamily: "inherit", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = lvl.color + "1A"}
                  onMouseLeave={e => e.currentTarget.style.background = isCurrent ? lvl.color + "1A" : "transparent"}
                >
                  <LIcon size={14} />
                  {fmt(lang, lvl.labelFr, lvl.labelEn)}
                  {isCurrent && <lucide.Check size={12} style={{ marginLeft: "auto" }} />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// PrincipalPicker — type-ahead search for users/groups
// =============================================================================
export function PrincipalPicker({ api, lang, THEME, onAdd, excludeIds }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ users: [], groups: [] });
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults({ users: [], groups: [] }); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/permissions/principals?q=${encodeURIComponent(query)}`);
        setResults({ users: res.users || [], groups: res.groups || [] });
        setShowResults(true);
      } catch (e) { console.error("Principal search:", e); }
      setSearching(false);
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, api]);

  function handleAdd(principal) {
    onAdd({
      principal_type: principal.principal_type,
      principal_id: principal.principal_id,
      principal_name: principal.principal_name,
      access_level: "read_only",
    });
    setQuery(""); setResults({ users: [], groups: [] }); setShowResults(false);
  }

  const filteredUsers = results.users.filter(u => !excludeIds.has(`user-${u.principal_id}`));
  const filteredGroups = results.groups.filter(g => !excludeIds.has(`group-${g.principal_id}`));
  const hasResults = filteredUsers.length > 0 || filteredGroups.length > 0;

  return (
    <div style={{ position: "relative" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", background: THEME.surface,
        border: `1px solid ${THEME.border}`, borderRadius: 8,
      }}>
        <lucide.Search size={16} color={THEME.textDim} />
        <input
          value={query} onChange={e => setQuery(e.target.value)}
          onFocus={() => query && setShowResults(true)}
          placeholder={fmt(lang, "Rechercher un utilisateur ou groupe...", "Search for users or groups...")}
          style={{
            flex: 1, border: "none", background: "transparent",
            color: THEME.text, fontSize: 13, outline: "none", fontFamily: "inherit",
          }}
        />
        {searching && <lucide.Loader2 size={14} color={THEME.textDim} className="spin" />}
      </div>
      <AnimatePresence>
        {showResults && hasResults && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
              background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`,
              borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              maxHeight: 300, overflow: "auto",
            }}
          >
            {filteredGroups.length > 0 && (
              <div>
                <div style={{ padding: "8px 14px", fontSize: 10, fontWeight: 700, color: THEME.textDim, textTransform: "uppercase", letterSpacing: 0.5, background: THEME.surface }}>
                  {fmt(lang, "Groupes", "Groups")}
                </div>
                {filteredGroups.map(g => (
                  <button
                    key={`g-${g.principal_id}`} type="button" onClick={() => handleAdd(g)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      padding: "10px 14px", border: "none", background: "transparent",
                      cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                      borderTop: `1px solid ${THEME.borderSubtle}`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = THEME.surfaceHover}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: THEME.accentDim, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <lucide.Users size={14} color={THEME.accent} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: THEME.text }}>{g.principal_name}</div>
                      {g.description && <div style={{ fontSize: 11, color: THEME.textDim }}>{g.description}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {filteredUsers.length > 0 && (
              <div>
                <div style={{ padding: "8px 14px", fontSize: 10, fontWeight: 700, color: THEME.textDim, textTransform: "uppercase", letterSpacing: 0.5, background: THEME.surface }}>
                  {fmt(lang, "Utilisateurs", "Users")}
                </div>
                {filteredUsers.map(u => (
                  <button
                    key={`u-${u.principal_id}`} type="button" onClick={() => handleAdd(u)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      padding: "10px 14px", border: "none", background: "transparent",
                      cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                      borderTop: `1px solid ${THEME.borderSubtle}`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = THEME.surfaceHover}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: getAvatarColor(u.principal_name), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                      {getInitials(u.principal_name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: THEME.text }}>{u.principal_name}</div>
                      <div style={{ fontSize: 11, color: THEME.textDim }}>{u.email} · {u.role}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// AclEntryRow — single row in the permissions list
// =============================================================================
export function AclEntryRow({ entry, onChangeAccess, onRemove, lang, THEME, readOnly }) {
  const isGroup = entry.principal_type === "group";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px", background: THEME.surface,
      border: `1px solid ${THEME.borderSubtle}`, borderRadius: 8,
    }}>
      <div style={{
        width: 32, height: 32,
        borderRadius: isGroup ? 8 : "50%",
        background: isGroup ? THEME.accentDim : getAvatarColor(entry.principal_name),
        display: "flex", alignItems: "center", justifyContent: "center",
        color: isGroup ? THEME.accent : "#fff",
        fontSize: 11, fontWeight: 700,
      }}>
        {isGroup ? <lucide.Users size={14} /> : getInitials(entry.principal_name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: THEME.text }}>{entry.principal_name}</div>
        <div style={{ fontSize: 11, color: THEME.textDim }}>
          {fmt(lang, isGroup ? "Groupe" : "Utilisateur", isGroup ? "Group" : "User")}
          {entry.locked && ` · ${fmt(lang, "Verrouillé", "Locked")}`}
        </div>
      </div>
      <AccessLevelDropdown
        value={entry.access_level}
        onChange={lvl => onChangeAccess(lvl)}
        lang={lang}
        disabled={entry.locked || readOnly}
        THEME={THEME}
      />
      {!entry.locked && !readOnly && (
        <button type="button" onClick={onRemove} style={{
          background: "transparent", border: "none", padding: 4,
          cursor: "pointer", color: THEME.textDim,
        }}>
          <lucide.X size={16} />
        </button>
      )}
    </div>
  );
}
